import { useRef } from 'react';
import { Sparkles, Send, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const QUICK_PROMPTS = [
  'Explain quadratic equations',
  'SAT reading strategies',
  'GED math tips',
  'WorkKeys preparation advice',
  'How to write a good essay',
];

export function DashboardChat({
  subjects,
  chatMessages,
  chatInput,
  setChatInput,
  chatLoading,
  chatSubject,
  setChatSubject,
  sendChatMessage,
}) {
  const chatEndRef = useRef(null);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-3 dashboard-widget flex flex-col h-[600px]">
        <div className="flex items-center justify-between pb-4 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#002FA7] rounded-sm flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-[#0A0A0A]">AI Study Assistant</h3>
              <p className="text-xs text-[#4B5563]">Powered by GPT-5.2</p>
            </div>
          </div>
          <Select value={chatSubject || "all"} onValueChange={(v) => setChatSubject(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px] rounded-sm" data-testid="chat-subject">
              <SelectValue placeholder="Any subject" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any subject</SelectItem>
              {subjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {chatMessages.length === 0 ? (
            <div className="text-center py-12">
              <Sparkles className="w-12 h-12 text-[#002FA7]/30 mx-auto mb-4" />
              <p className="text-[#4B5563]">Ask me anything about your studies!</p>
              <p className="text-sm text-[#9CA3AF] mt-2">I can help explain concepts, solve problems, and provide study tips.</p>
            </div>
          ) : (
            chatMessages.map((msg, i) => (
              <div key={`chat-${msg.role}-${i}`} className={msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            ))
          )}
          {chatLoading && (
            <div className="chat-bubble-ai">
              <div className="flex items-center gap-2">
                <div className="spinner w-4 h-4"></div>
                <span>Thinking...</span>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <div className="pt-4 border-t border-[#E5E7EB]">
          <div className="flex gap-2">
            <Input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
              placeholder="Ask a question..."
              className="rounded-sm"
              data-testid="chat-input"
            />
            <Button
              onClick={sendChatMessage}
              disabled={chatLoading || !chatInput.trim()}
              className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm"
              data-testid="chat-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      <div className="dashboard-widget h-fit">
        <h4 className="font-bold text-[#0A0A0A] mb-4">Quick Prompts</h4>
        <div className="space-y-2">
          {QUICK_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              onClick={() => setChatInput(prompt)}
              className="w-full text-left p-3 text-sm text-[#4B5563] hover:bg-[#F3F4F6] rounded-sm transition-colors"
            >
              <ChevronRight className="w-4 h-4 inline mr-2" />{prompt}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
